import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import {
  FaBolt,
  FaProjectDiagram,
  FaBrain,
  FaNetworkWired,
  FaLightbulb,
  FaLayerGroup,
} from 'react-icons/fa';

const elements = [
  {
    icon: <FaBrain className="text-4xl text-cyan-400" />,
    title: 'MASRIQ = The Brain',
    desc: 'The intelligent center guiding all operations — legacy + logic',
  },
  {
    icon: <FaNetworkWired className="text-4xl text-purple-400" />,
    title: 'NextTech = Neurons',
    desc: 'Tech tools & platforms firing code, data, and APIs',
  },
  {
    icon: <FaBolt className="text-4xl text-yellow-300" />,
    title: 'Team = Synapses',
    desc: 'Youthful connectors driving real-time innovation',
  },
  {
    icon: <FaLightbulb className="text-4xl text-pink-400" />,
    title: 'Client = Signal',
    desc: 'Input spark that activates the neural system',
  },
  {
    icon: <FaProjectDiagram className="text-4xl text-green-400" />,
    title: 'Solutions = Thoughts',
    desc: 'Refined, intelligent outcomes like apps, platforms, and APIs',
  },
  {
    icon: <FaBolt className="text-4xl text-orange-400" />,
    title: 'AI Models = Memory Cells',
    desc: 'Self-learning systems evolving over time',
  },
];

const neuralLayers = [
  {
    layer: 'Input Layer',
    meaning: '🎯 Client Needs / Market Gaps',
  },
  {
    layer: 'Hidden Layers',
    meaning: '👩‍💻 MASRIQ Team — Engineers, Designers, ML Models',
  },
  {
    layer: 'Weights/Biases',
    meaning: '📊 Experience, Data Patterns, Logic Pipelines',
  },
  {
    layer: 'Activation',
    meaning: '⚙️ Internal Decision-Making, AI Systems',
  },
  {
    layer: 'Output Layer',
    meaning: '🚀 Final Product (App, Platform, API, Insight)',
  },
];

const NeuralSystem = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <section className="py-20 px-6 bg-[#0b0c1c] text-white" id="neural-system">
      {/* Meaning of MASRIQ */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="mb-12 text-center"
      >
        <h2 className="text-4xl md:text-5xl font-bold text-cyan-400 mb-4">
          Meaning of <span className="text-purple-400">MASRIQ</span>
        </h2>
        <p className="text-lg max-w-2xl mx-auto text-gray-300">
          The East &rarr; Symbolizes{' '}
          <span className="text-cyan-400">Rising</span>, a{' '}
          <span className="text-purple-400">New Dawn</span>, and a{' '}
          <span className="text-pink-400">Fresh Perspective</span>.
        </p>
      </motion.div>

      {/* Metaphorical Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 max-w-6xl mx-auto mb-16">
        {elements.map((item, index) => (
          <motion.div
            key={index}
            data-aos="fade-up"
            data-aos-delay={index * 100}
            whileHover={{ scale: 1.05, rotate: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="bg-[#111225] rounded-2xl shadow-lg border border-cyan-600 hover:shadow-cyan-500/50 p-6 text-center backdrop-blur-md bg-opacity-50 hover:scale-[1.03] transition-transform duration-300"
          >
            <div className="mb-4">{item.icon}</div>
            <h3 className="text-xl font-semibold text-cyan-300 mb-2">
              {item.title}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              {item.desc}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Neural Layer Analogy */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="text-center mb-12"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-cyan-400 mb-6">
          Neural Layer Analogy for Business Operations
        </h2>
        <p className="text-gray-400 max-w-3xl mx-auto">
          A neural network inspired metaphor to explain MASRIQ’s structure and decision-making intelligence.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {neuralLayers.map((layer, index) => (
          <motion.div
            key={index}
            data-aos="fade-up"
            data-aos-delay={index * 100}
            whileHover={{ scale: 1.05 }}
            className="bg-[#111225] border border-purple-600 rounded-xl p-5 text-center shadow-md hover:shadow-purple-500/40 transition-all duration-300"
          >
            <FaLayerGroup className="text-3xl mx-auto mb-3 text-purple-400" />
            <h3 className="text-xl font-semibold text-purple-300 mb-2">
              {layer.layer}
            </h3>
            <p className="text-gray-300 text-sm">{layer.meaning}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default NeuralSystem;
